import React, { createContext, useContext, useEffect, useState } from "react";
import { api, clearToken, getToken, setToken } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const { lang, applyAccountLang } = useLang();

  // Apply the language saved on the account (cross-device persistence) whenever
  // the profile loads with a stored preference.
  useEffect(() => {
    const accountLang = partner?.language;
    if (accountLang && accountLang !== lang) applyAccountLang(accountLang, partner?.language_manual);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partner?.language, partner?.language_manual]);

  useEffect(() => {
    (async () => {
      const t = getToken();
      if (t) {
        try {
          setPartner(await api("/auth/me"));
        } catch {
          clearToken();
        }
      }
      setLoading(false);
    })();
  }, []);

  async function login(email, password, captcha_token, remember = false) {
    const res = await api("/auth/login", {
      method: "POST",
      body: { email, password, captcha_token },
      auth: false,
    });
    setToken(res.access_token, remember);
    setPartner(res.partner);
    return res.partner;
  }

  async function register(email, password, org_name, country, referral_code, captcha_token, city, category, siret, vat_number, cgu_accepted, first_name, google_token) {
    const res = await api("/auth/register", {
      method: "POST",
      body: { email, password, org_name, first_name, country, referral_code, captcha_token, city, category, siret, vat_number, cgu_accepted, google_token },
      auth: false,
    });
    setToken(res.access_token);
    setPartner(res.partner);
    return res.partner;
  }

  async function loginWithGoogleSession(session_id) {
    const res = await api("/auth/google/session", {
      method: "POST",
      body: { session_id },
      auth: false,
    });
    if (res.mode === "signup") return res;
    setToken(res.access_token);
    setPartner(res.partner);
    return res;
  }

  function logout() {
    clearToken();
    setPartner(null);
  }

  async function refresh() {
    try {
      setPartner(await api("/auth/me"));
    } catch {}
  }

  return (
    <AuthContext.Provider
      value={{ partner, loading, login, register, loginWithGoogleSession, logout, refresh, setPartner }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const c = useContext(AuthContext);
  if (!c) throw new Error("AuthProvider missing");
  return c;
}
